// This is a reusable, decoupled table UI shell powered by tanstack which handles loading states,
// selection logic and pagination, fallback messages and row UI.

import * as React from "react";

// Reusable component
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Spinner } from "components/ui/Spinner";
import EmptyStateCard from "./EmptyStateCard";

// inhouse icons
import { Box, ArrowUp, ArrowDown } from "@/components/icons_new";

export function SecureTableShell({ 
  data, 
  columns, 
  renderRow, 
  rowKey="id", 
  fallbackState,
  fallbackMessages = {
    noData: {
      title: "No data available",
      description: "Add an item to see it here.",
      buttonLabel: "Add item",
    },
    noMatch: {
      title: "No matching results",
      description: "Try adjusting your filters or search.",
      buttonLabel: "Reset filters",
    },
  },
  onActionFallback,
  onSelectionChange = () => {},
}) {
//   TODO: Implement sorting with connection to backend
  const [sorting, setSorting] = React.useState([]);
  const [rowSelection, setRowSelection] = React.useState({});

  // import the table logic from tanstack
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
    },
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    // TODO: Remove this once connected to the sorting logic in backend
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    // Allow the parent components to get the selected rows everytime they change
    onRowSelectionChange: (updater) => {
      const updated = typeof updater === "function" ? updater(rowSelection) : updater;
      setRowSelection(updated);
      const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
      onSelectionChange(selectedRows);
    },
    getRowId: (row) => row[rowKey],
  });
  
  return (
    <div className="w-full overflow-auto rounded-b-xl bg-white border border-gray-200 dark:border-gray-700">
      <Table>
        {/* Render header for the table */}
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-[#F9F9F9]">
              {headerGroup.headers.map((header) => (
                <TableHead className="px-4 py-4 text-[#5A5A5A] font-semibold" key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())
                  }
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        {/* Render body of the table */}
        <TableBody>
          {fallbackState === "loading" ? (
            // loading state
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center">
                <Spinner size="small">
                  <p className="text-sm text-gray-500 mt-2">Loading table</p>
                </Spinner>
              </TableCell>
            </TableRow>

          )  : fallbackState === "noData" ? (

            // Fallback for no data at all
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center">
                <EmptyStateCard
                  icon={
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full">
                      <Box className="w-8 h-8 text-[#000]" />
                    </div>
                  }
                  title={fallbackMessages.noData.title}
                  description={fallbackMessages.noData.description}
                  buttonLabel={fallbackMessages.noData.buttonLabel}
                  onAction={onActionFallback}
                />
              </TableCell>
            </TableRow>

          ) : fallbackState === "noMatch" ? (

            // Fallback for No match after search or filter
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center">
                <EmptyStateCard
                  icon={
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full">
                      <Box className="w-8 h-8 text-[#000]" />
                    </div>
                  }
                  title={fallbackMessages.noMatch.title}
                  description={fallbackMessages.noMatch.description}
                  buttonLabel={fallbackMessages.noMatch.buttonLabel}
                  onAction={onActionFallback}
                />
              </TableCell>
            </TableRow>
          ) : (

            // show data in table if available
            table.getRowModel().rows.map((row) => renderRow(row))
          )}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      {/* TODO: Decouple this and lay out its logic */}
      <div className="flex items-center justify-between px-4 py-3 text-sm text-muted-foreground">
        <div>
          {table.getSelectedRowModel().rows.length} of {table.getRowModel().rows.length} row(s) selected.
        </div>  
        <div className="flex items-center gap-2">
          <button
            className="px-2 py-1 border rounded disabled:opacity-50"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </button>
          <button
            className="px-2 py-1 border rounded disabled:opacity-50"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
